import { Trans, t } from "@web-monorepo/i18n/macro";
import { DDSIconButton } from "@web-monorepo/dds";
import { IconArrowLeft, IconArrowRight, IconShuffle } from "@web-monorepo/dds-icons";
import { clsx } from "clsx";
import type { Person } from "components/PersonListItem";
import { logParentToolkitEvent } from "src/utils/logEvent";
import ShufflingAvatars from "./ShufflingAvatars";
import styles from "./WhoseTurnCardContent.module.css";
import {
  useWhoseTurnCardContent,
  type UseWhoseTurnCardContentProps,
  type UseWhoseTurnCardContentResult,
} from "./useWhoseTurnCardContent.vm";

type WhoseTurnCardContentProps = {
  persons: Person[];
  /**
   * Dependency injection for the view model — Storybook stories override this
   * with a stub `useVm` returning canned state. Production callers leave the
   * default.
   */
  di?: {
    useVm?: (props: UseWhoseTurnCardContentProps) => UseWhoseTurnCardContentResult;
  };
};

export default function WhoseTurnCardContent({ persons, di }: WhoseTurnCardContentProps) {
  const useVm = di?.useVm ?? useWhoseTurnCardContent;
  const {
    shuffling,
    loadingAvatar,
    currentPerson,
    previousPerson,
    nextPerson,
    currentIndex,
    showFirstShuffledPerson,
    reshuffle,
    moveToPreviousPerson,
    moveToNextPerson,
  } = useVm({ persons });

  if (shuffling || !currentPerson) {
    return <ShufflingAvatars avatar={loadingAvatar} />;
  }

  const clickShuffle = () => {
    logParentToolkitEvent({ eventName: `whose_turn.shuffle.tap` });
    reshuffle();
  };

  return (
    <div className={styles.container}>
      <div className={clsx(styles.nameplate, showFirstShuffledPerson ? styles.namePrimary : styles.nameLight)}>
        <Trans>It's {{ name: currentPerson.firstName }}'s turn!</Trans>
      </div>
      <div className={styles.avatarRow}>
        {currentIndex > 0 && previousPerson && (
          <img src={previousPerson.avatar} alt="" className={styles.secondaryAvatarPrev} />
        )}
        <img src={currentPerson.avatar} alt="" className={styles.primaryAvatar} />
        {nextPerson && <img src={nextPerson.avatar} alt="" className={styles.secondaryAvatarNext} />}
      </div>
      <div className={styles.controls}>
        {currentIndex > 0 && (
          <div className={styles.previousSlot}>
            <DDSIconButton
              data-name="whose_turn:previous"
              aria-label={t`Previous`}
              tooltip={{ content: t`Previous person`, dataName: "whose_turn:previous:tooltip" }}
              variant="tertiary"
              onClick={moveToPreviousPerson}
            >
              <IconArrowLeft />
            </DDSIconButton>
          </div>
        )}
        <div className={styles.shuffleWrap}>
          <DDSIconButton
            data-name="whose_turn:shuffle"
            aria-label={t`Shuffle`}
            tooltip={{ content: t`Shuffle`, dataName: "whose_turn:shuffle:tooltip" }}
            variant="secondary"
            size="large"
            onClick={clickShuffle}
          >
            <IconShuffle />
          </DDSIconButton>
        </div>
        <div className={styles.nextSlot}>
          <DDSIconButton
            data-name="whose_turn:next"
            aria-label={t`Next`}
            tooltip={{ content: t`Next person`, dataName: "whose_turn:next:tooltip" }}
            variant="tertiary"
            onClick={moveToNextPerson}
          >
            <IconArrowRight />
          </DDSIconButton>
        </div>
      </div>
    </div>
  );
}
